import React, { useState, useEffect } from 'react';
import { ArrowRight, MapPin } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { popularDestinations } from '../data/destinations';

const PopularDestinations = () => {
  const { language } = useLanguage();
  const [prices, setPrices] = useState({});

  useEffect(() => {
    // Calculate real prices for each destination
    popularDestinations.forEach(dest => {
      calculatePrice(dest.route.from, dest.route.to, dest.id);
    });
  }, []);

  const calculatePrice = async (from, to, destId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/calculate-price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_location: from,
          to_location: to,
          time: '12:00'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPrices(prev => ({ ...prev, [destId]: data.final_price }));
      }
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const handleDestinationClick = (dest) => {
    // Scroll to booking form
    const bookingSection = document.querySelector('#booking-form');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Pre-fill booking form after scroll
      setTimeout(() => {
        // Dispatch custom event to fill form
        window.dispatchEvent(new CustomEvent('fillBookingForm', {
          detail: {
            from: dest.route.from,
            to: dest.route.to
          }
        }));
      }, 800);
    }
  };

  return (
    <section className="py-20 bg-gray-950">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block">
            <span className="text-amber-400 font-semibold text-sm uppercase tracking-wider mb-2 block">
              {language === 'ro' ? 'Destinații Populare' : 'Destinazioni Popolari'}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {language === 'ro' ? 'Destinații' : 'Destinazioni'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">{language === 'ro' ? 'Principale' : 'Principali'}</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto"></div>
          </div>
        </div>

        {/* Destinations Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {popularDestinations.map((dest, index) => (
            <Card 
              key={dest.id}
              onClick={() => handleDestinationClick(dest)}
              data-testid={`popular-destination-card-${dest.id}`}
              className="group bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800 hover:border-amber-500/50 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/10 hover:-translate-y-2 cursor-pointer"
              style={{ animationDelay: `${index * 80}ms` }}
            >
              {/* Destination Image */}
              <div className="relative h-56 overflow-hidden">
                <img 
                  src={dest.image}
                  alt={dest.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"></div>
                
                {/* Country Flag */}
                <div className="absolute top-4 right-4">
                  <span className={`text-2xl`}>
                    {dest.country === 'it' ? '🇮🇹' : '🇫🇷'}
                  </span>
                </div>

                {/* Destination Name */}
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-3xl font-bold text-white mb-1">
                    {language === 'ro' ? dest.nameRo : dest.name}
                  </h3>
                  <Badge variant="outline" className="border-amber-500/30 text-amber-400 bg-amber-500/10">
                    {language === 'ro' ? dest.typeRo : dest.typeIt}
                  </Badge>
                </div>
              </div>

              {/* Price & CTA */}
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">
                      {language === 'en' ? 'From' : 'Da'}
                    </p>
                    <p className="text-3xl font-bold text-amber-400">
                      €{prices[dest.id] || dest.priceFrom}
                    </p>
                    {prices[dest.id] && (
                      <p className="text-xs text-gray-500 mt-1">
                        {language === 'en' ? 'Real-time price' : 'Prezzo in tempo reale'}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    className="border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-black transition-all duration-300"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* View All Button */}
        <div className="mt-12 text-center">
          <Button
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold px-8 py-6 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-amber-500/50"
          >
            <MapPin className="w-5 h-5 mr-2" />
            {language === 'ro' ? 'Vezi Toate Destinațiile' : 'Vedi Tutte le Destinazioni'}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PopularDestinations;
